import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, Loader2, ArrowRight, Home } from 'lucide-react';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import AnimatedBackground from '../components/AnimatedBackground';

const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });

  const from = location.state?.from || '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast.error(t('auth.fillAllFields'));
      return;
    }
    setLoading(true);
    try {
      const userData = await login(formData.email, formData.password);
      toast.success(t('auth.loginSuccess'));
      if (userData.role === 'admin') navigate('/admin');
      else if (userData.role === 'organizer') navigate('/organizer');
      else if (userData.role === 'provider') navigate('/provider');
      else if (userData.role === 'sponsor') navigate('/sponsor-dashboard');
      else navigate(from);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatedBackground>
      {/* Mini nav */}
      <div className="fixed top-0 left-0 right-0 z-50 px-6 py-3 flex items-center justify-between">
        <Link to="/">
          <img src="/logo-light.png" alt="iBookSport" className="h-8" />
        </Link>
        <Link to="/" className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm font-heading font-bold uppercase tracking-wider transition-colors" data-testid="login-home-link">
          <Home className="w-4 h-4" /> Accueil
        </Link>
      </div>
      <div className="min-h-screen flex items-center justify-center p-4" data-testid="login-page">
        <div className="w-full max-w-md">
          {/* Logo */}
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-center mb-8">
            <Link to="/">
              <img src="/logo-light.png" alt="iBookSport" className="h-12 mx-auto" data-testid="login-logo" />
            </Link>
          </motion.div>

          {/* Card */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.08] overflow-hidden">

            {/* Header bar */}
            <div className="bg-brand px-8 py-5">
              <h1 className="font-heading text-xl font-black uppercase tracking-wider text-white">{t('auth.loginTitle')}</h1>
              <p className="text-white/70 text-sm mt-1">{t('auth.loginSubtitle')}</p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-8 space-y-5">
              <div>
                <Label className="text-[10px] font-heading uppercase tracking-widest text-slate-400 mb-1.5 block">{t('auth.email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 w-4 h-4" />
                  <input type="email" placeholder={t('auth.emailPlaceholder')} value={formData.email}
                    onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                    className="w-full h-12 pl-11 pr-4 bg-white/[0.06] border border-white/[0.1] text-white placeholder:text-slate-600 outline-none text-sm focus:border-brand/60 focus:bg-white/[0.08] transition-all"
                    data-testid="email-input" />
                </div>
              </div>

              <div>
                <Label className="text-[10px] font-heading uppercase tracking-widest text-slate-400 mb-1.5 block">{t('auth.password')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 w-4 h-4" />
                  <input type={showPassword ? 'text' : 'password'} placeholder={t('auth.passwordPlaceholder')} value={formData.password}
                    onChange={(e) => setFormData(p => ({ ...p, password: e.target.value }))}
                    className="w-full h-12 pl-11 pr-11 bg-white/[0.06] border border-white/[0.1] text-white placeholder:text-slate-600 outline-none text-sm focus:border-brand/60 focus:bg-white/[0.08] transition-all"
                    data-testid="password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <Link to="/forgot-password" className="text-xs text-slate-500 hover:text-brand transition-colors" data-testid="forgot-password-link">
                  Mot de passe oublie ?
                </Link>
              </div>

              <button type="submit" disabled={loading} data-testid="submit-login-btn"
                className="w-full h-12 bg-brand hover:bg-brand/90 text-white font-heading font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2 transition-all disabled:opacity-50 group">
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> {t('auth.signingIn')}</>
                ) : (
                  <>{t('auth.loginButton')} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></>
                )}
              </button>
            </form>

            {/* Footer */}
            <div className="border-t border-white/[0.06] px-8 py-5 text-center">
              <p className="text-sm text-slate-500">
                {t('auth.noAccount')}{' '}
                <Link to="/register" className="text-brand font-heading font-bold uppercase text-xs tracking-wider hover:text-brand/80 transition-colors">
                  {t('auth.signUp')}
                </Link>
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatedBackground>
  );
};

export default LoginPage;
